import React, { useState } from 'react';
import { Task } from '../../types/task';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { Input } from '../ui/input';

interface TaskSelectorProps {
  tasks: Task[];
  selectedTask: Task | null;
  onChange: (task: Task | null) => void;
  className?: string;
  placeholder?: string;
}

export const TaskSelector: React.FC<TaskSelectorProps> = ({
  tasks,
  selectedTask,
  onChange,
  className,
  placeholder = "Select a task",
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  

  const filteredTasks = tasks
    .filter(task =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => b.createdAt - a.createdAt); // Show newest tasks first

  return (
    <Listbox value={selectedTask} onChange={onChange}>
      <div className={cn("relative", className)}>
        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-background py-2 pl-3 pr-10 text-left border-2 border-input hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/20">
          <span className="block truncate">
            {selectedTask ? (
              <div className="flex items-center gap-2">
                <span className={cn(
                  "w-2 h-2 rounded-full",
                  {
                    'bg-yellow-500': selectedTask.priority === 'low',
                    'bg-orange-500': selectedTask.priority === 'medium',
                    'bg-red-500': selectedTask.priority === 'high',
                  }
                )} />
                {selectedTask.title}
              </div>
            ) : (
              placeholder
            )}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Listbox.Options className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg bg-card py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-border backdrop-blur-xl">
          {/* Search Input */}
          <div className="sticky top-0 p-2 bg-background/50 backdrop-blur-sm border-b border-border">
            <div className="relative">
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          <div className="max-h-60 overflow-auto">
            <Listbox.Option
              value={null}
              className={({ active }) =>
                cn(
                  'relative cursor-pointer select-none py-2 pl-10 pr-4',
                  active ? 'bg-primary/10 text-foreground' : 'text-foreground'
                )
              }
            >
              {({ selected }) => (
                <>
                  <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                    No Task
                  </span>
                  {selected ? (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </Listbox.Option>

            {filteredTasks.map((task) => (
              <Listbox.Option
                key={task.id}
                value={task}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none py-2 pl-10 pr-4',
                    active ? 'bg-primary/10 text-foreground' : 'text-foreground'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "w-2 h-2 rounded-full",
                        {
                          'bg-yellow-500': task.priority === 'low',
                          'bg-orange-500': task.priority === 'medium',
                          'bg-red-500': task.priority === 'high',
                        }
                      )} />
                      <div>
                        <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                          {task.title}
                        </span>
                        {task.description && (
                          <span className="block truncate text-sm text-muted-foreground">
                            {task.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}

            {filteredTasks.length === 0 && (
              <div className="py-2 px-4 text-sm text-muted-foreground text-center">
                No tasks found
              </div>
            )}
          </div>
        </Listbox.Options>
      </div>
    </Listbox>
  );
};